import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { Language, ApplicationType } from '../types/translation-types';
import { ApplicationLanguage } from '../types/language-management-types';

interface ApplicationLanguagesStore {
  // Data
  applicationLanguages: ApplicationLanguage[];
  applicationType: ApplicationType | null;
  applicationName: string;
  availableLanguages: Language[];

  // UI States
  isLoading: boolean;
  isLoadingAvailable: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;
  hasUnsavedChanges: boolean;

  // Pending changes (before save)
  pendingLanguageIds: number[];

  // Actions
  fetchApplicationLanguages: (applicationType: ApplicationType) => Promise<void>;
  fetchAvailableLanguages: () => Promise<void>;
  addLanguage: (languageId: number) => void;
  removeLanguage: (languageId: number) => void;
  reorderLanguages: (languageIds: number[]) => void;
  saveChanges: () => Promise<void>;
  discardChanges: () => void;
  setApplicationType: (applicationType: ApplicationType) => Promise<void>;
  reset: () => void;
}

export const useApplicationLanguagesStore = create<ApplicationLanguagesStore>((set, get) => ({
  // Initial state
  applicationLanguages: [],
  applicationType: null,
  applicationName: '',
  availableLanguages: [],
  isLoading: false,
  isLoadingAvailable: false,
  isSaving: false,
  error: null,
  saveError: null,
  hasUnsavedChanges: false,
  pendingLanguageIds: [],

  // Actions
  fetchApplicationLanguages: async (applicationType: ApplicationType) => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getApplicationLanguagesUseCase.execute(applicationType);

      if (response.isSuccess) {
        const languageIds = response.languages.map((l) => l.languageId);
        set({
          applicationLanguages: response.languages,
          applicationType: response.applicationType,
          applicationName: response.applicationName,
          pendingLanguageIds: languageIds,
          hasUnsavedChanges: false,
          isLoading: false,
        });
      } else {
        set({
          error: response.errorMessage || 'Failed to load application languages',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load application languages',
        isLoading: false,
      });
    }
  },

  fetchAvailableLanguages: async () => {
    set({ isLoadingAvailable: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getAllLanguagesUseCase.execute();

      if (response.isSuccess) {
        // Only show active languages
        const activeLanguages = response.rows.filter((l) => l.isActive);
        set({
          availableLanguages: activeLanguages,
          isLoadingAvailable: false,
        });
      } else {
        set({ isLoadingAvailable: false });
      }
    } catch (error) {
      set({ isLoadingAvailable: false });
    }
  },

  addLanguage: (languageId: number) => {
    const { pendingLanguageIds, applicationLanguages } = get();
    if (!pendingLanguageIds.includes(languageId)) {
      const originalIds = applicationLanguages.map((l) => l.languageId);
      const newPendingIds = [...pendingLanguageIds, languageId];
      const hasChanges =
        newPendingIds.length !== originalIds.length ||
        !newPendingIds.every((id, idx) => originalIds[idx] === id);

      set({
        pendingLanguageIds: newPendingIds,
        hasUnsavedChanges: hasChanges,
      });
    }
  },

  removeLanguage: (languageId: number) => {
    const { pendingLanguageIds, applicationLanguages } = get();
    const originalIds = applicationLanguages.map((l) => l.languageId);
    const newPendingIds = pendingLanguageIds.filter((id) => id !== languageId);
    const hasChanges =
      newPendingIds.length !== originalIds.length ||
      !newPendingIds.every((id, idx) => originalIds[idx] === id);

    set({
      pendingLanguageIds: newPendingIds,
      hasUnsavedChanges: hasChanges,
    });
  },

  reorderLanguages: (languageIds: number[]) => {
    const { applicationLanguages } = get();
    const originalIds = applicationLanguages.map((l) => l.languageId);
    const hasChanges =
      languageIds.length !== originalIds.length ||
      !languageIds.every((id, idx) => originalIds[idx] === id);

    set({
      pendingLanguageIds: languageIds,
      hasUnsavedChanges: hasChanges,
    });
  },

  saveChanges: async () => {
    const { applicationType, pendingLanguageIds } = get();
    if (!applicationType) return;

    set({ isSaving: true, saveError: null });
    try {
      const services = servicesContainer.getServices();
      await services.setApplicationLanguagesUseCase.execute(applicationType, pendingLanguageIds);

      // Refresh to get updated data
      await get().fetchApplicationLanguages(applicationType);

      set({ isSaving: false, hasUnsavedChanges: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save changes';
      set({ saveError: message, isSaving: false });
      throw error;
    }
  },

  discardChanges: () => {
    const { applicationLanguages } = get();
    const originalIds = applicationLanguages.map((l) => l.languageId);
    set({
      pendingLanguageIds: originalIds,
      hasUnsavedChanges: false,
      saveError: null,
    });
  },

  setApplicationType: async (applicationType: ApplicationType) => {
    const { fetchApplicationLanguages, fetchAvailableLanguages } = get();
    set({ applicationType });
    await Promise.all([fetchApplicationLanguages(applicationType), fetchAvailableLanguages()]);
  },

  reset: () => {
    set({
      applicationLanguages: [],
      applicationType: null,
      applicationName: '',
      availableLanguages: [],
      isLoading: false,
      isLoadingAvailable: false,
      isSaving: false,
      error: null,
      saveError: null,
      hasUnsavedChanges: false,
      pendingLanguageIds: [],
    });
  },
}));
